import React, { createContext, useContext, useState, useEffect } from "react"
import de from "../locales/de.json"
import en from "../locales/en.json"
import es from "../locales/es.json"

const translations = { de, en, es }

const LanguageContext = createContext()

export const useLanguage = () => useContext(LanguageContext)

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("de")

  useEffect(() => {
    // 1. Check URL parameters for explicit language routing (e.g. ?lang=en)
    const urlParams = new URLSearchParams(window.location.search)
    let urlLang = urlParams.get("lang")

    // Fallback: If someone uses #attitude?lang=en
    if (!urlLang && window.location.hash.includes("lang=")) {
      const hashParams = new URLSearchParams(window.location.hash.split("?")[1])
      urlLang = hashParams.get("lang")
    }

    if (urlLang && translations[urlLang]) {
      setLanguage(urlLang)
      localStorage.setItem("status-language", urlLang)
      return // Priority given to URL
    }

    // 2. Check local storage for saved language
    const savedLanguage = localStorage.getItem("status-language")
    if (savedLanguage && translations[savedLanguage]) {
      setLanguage(savedLanguage)
    }
  }, [])

  // Keep <html lang> in sync with the active language. The SSR default stays
  // "de" (correct for crawlers reading the static HTML and for the canonical),
  // but once a visitor switches language client-side, assistive tech and
  // JS-rendering crawlers get the right signal. No URL change involved.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language
    }
  }, [language])

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang)
      localStorage.setItem("status-language", lang)
    }
  }

  const t = (key) => {
    if (!key) return ""
    const keys = key.split('.')
    let result = translations[language]
    for (const k of keys) {
      if (!result || result[k] === undefined) {
        return key // Return key if translation not found
      }
      result = result[k]
    }
    return result
  }

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}
